import { logDebug, logError } from "../utils"

export type Prior = {
  data: Record<string, [number, number]>,
  paths: string[],
};

export function conditionallyLoadPriors(siteId: string) {
  const priors = loadCachedPriors(siteId);
  if (priors === null) {
    // The priors haven't been loaded. Get them from the server.
    performAsyncLoad(siteId);
    logDebug("Loading priors, none existed yet");
  } else {
    dispatchPriorsLoaded();
  }
}

function performAsyncLoad(siteId: string) {
  let domain = "";
  if (!siteId.startsWith("local_")) {
    domain = "https://cdn.version.compare";
  }

  fetch(`${domain}/api/site/${siteId}/state.json`, {
    method: 'GET',
    mode: 'cors',
    credentials: "omit",
    priority: "high",
  })
    .then(response => {
      switch (response.status) {
        case 200:
          return response.json();

        case 404:
          logError(`Site ID '${siteId}' is invalid. Please double check your ID or register an account on https://version.compare`);
          break;

        default:
          logError(`Unexpected status retrieving experimental data from version.compare: ${response.status}. Aborting`);
      }
    })
    .then((data) => {
      sessionStorage.setItem(key(siteId), JSON.stringify(data));
      dispatchPriorsLoaded();
    })
    .catch(error => {
      logError("Failed to load experimental data. Aborting.");
      console.error(error)
    });
}

export function getPriors(siteId: string): Record<string, Prior> {
  const priors = loadCachedPriors(siteId);
  if (priors === null) {
    logDebug("Priors were not loaded. Please call `conditionallyLoadPriors` first");
    return {};
  }

  return priors;
}

function loadCachedPriors(siteId: string): Record<string, Prior> | null {
  const priors = sessionStorage.getItem(key(siteId));
  if (priors !== null) {
    return JSON.parse(priors);
  }
  return null;
}

function key(siteId: string) {
  return `vc_state_${siteId}`;
}

const loadedEventName = "versioncompareDataLoaded";

function dispatchPriorsLoaded() {
  window.dispatchEvent(new CustomEvent(loadedEventName));
}

export function awaitPriorsLoaded(siteId: string, callback: (() => any)) {
  window.addEventListener(loadedEventName, callback);
  conditionallyLoadPriors(siteId);
}

export function stopAwaitingPriorsLoaded(callback: (() => any)) {
  window.removeEventListener(loadedEventName, callback);
}